import { GarmentPieceFabrics, type SeamTape } from '@seddi/scene';
import { PropsDiff } from '../core/reconciler/propsDiff';
import {
  type SeamTapeRenderable,
  attachFabric,
  detachFabric,
  updateGeometry,
  updateGrain,
} from '../renderables/seamTapeRenderable';
import { getFabricColorId } from '../resources/fabric';
import type { FabricWithColor } from '../resources/fabricTypes';
import type { SceneReconcilerFrame } from './sceneReconcilerFrame';

export const update = (
  renderable: SeamTapeRenderable,
  nextProps: SeamTape,
  prevProps: SeamTape | undefined,
  frame: SceneReconcilerFrame,
) => {
  const diffProps = PropsDiff.diff(nextProps, prevProps);
  let dirty = false;

  const isGeometryDiff = renderable.garmentGeometry !== frame.garmentGeometry;

  // Update geometry.
  if (diffProps.includes('geometry') || isGeometryDiff) {
    const id = renderable.id;
    const virtualPiece = frame.garmentGeometry.virtualPieces.find((p) => p.id === id);
    updateGeometry(renderable, virtualPiece);
    dirty = true;
  }
  // Update fabric.
  if (diffProps.includes('fabric') || diffProps.includes('fabricColor') || isGeometryDiff) {
    dirty = updateFabric(renderable, nextProps, prevProps, frame) || dirty;
  }
  // Update grain.
  if ((diffProps.includes('grain') || isGeometryDiff) && renderable.fabricRef) {
    updateGrain(renderable, nextProps.grain);
    dirty = true;
  }

  return dirty;
};

/**
 * Update fabric.
 * @param renderable - Renderable.
 * @param nextProps - Tape prev.
 * @param prevProps - Tape next.
 * @param frame - Reconciler frame.
 */
const updateFabric = (
  renderable: SeamTapeRenderable,
  nextProps: SeamTape | undefined,
  prevProps: SeamTape | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  const { resources, nextScene } = frame;

  if (prevProps) {
    detachFabric(renderable, resources);
  }
  if (!nextProps) {
    return true;
  }

  const fabricColorId = getFabricColorId(nextProps.fabric, nextProps.fabricColor);
  let dirty = false;

  if (resources.findFabric(fabricColorId)) {
    attachFabric(renderable, fabricColorId, nextProps.grain, frame.resources);
    dirty = true;
  } else {
    if (!resources.hasFabricRequest(fabricColorId)) {
      const fabric = GarmentPieceFabrics.FindFabricInScene(nextScene, nextProps.fabric);
      const fabricWithColor: FabricWithColor = {
        ...fabric,
        id: fabricColorId,
        recolorTarget: nextProps.fabricColor,
      };
      resources.loadFabricDeferred(fabricWithColor);
    }
  }

  return dirty;
};
